import * as React from 'react';
import { translate } from '@data/translations';
import { ReservationController, Reservation } from '../resController';
import { CustomersAndCompanies } from './CustomersAndCompanies';
import CustomerForm from '../../customers/form';
import { setModalContent, closeModal } from '@components/modal';
import {
    CustomerModel,
    ReservationGuestReferenceModel,
    ReservationStatus,
    TaxModel,
    GuestTaxConfig,
    ResidenceTaxReductionCodeCISU,
} from '@common/modelDefinition';
import VisaDataModal from '../../customers/visaDataModal';
import Delete from '@components/confirmModal';
import dayjs from 'dayjs';
//@ts-ignore
import countryCodes from '@common/countryCodes';
import ConfirmButtons from '@components/buttons/confirmButtons';
import Tooltip from '@components/tooltip/tooltip';
import { toast } from 'react-toastify';
import { ReactComponent as Edit } from '../../../assets/custom/icons/edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/custom/icons/trash.svg';
import { ReactComponent as Report } from '../../../assets/custom/icons/reports.svg';
import ContextMenu from '@components/ContextMenu';
import useStore from '@data/state/zustand';
import { CustomDayPicker } from '@components/DayPicker';

// IMPORT IZ UTILS
import {
    getSuggestedItemByAge,
    parseGuestTaxConfig,
    getDropdownOptions,
    getDropdownValue,
    parseDropdownValue,
    DefaultPosItem,
    DropdownOption,
    getTaxAmountInfoLocal,
} from '../utils';
import { saveOrUpdateReservation } from '../api';
import PermissionCheck from '@components/PermissionCheck';

const ctrl = new ReservationController();

// ============================================================================
// HELPER: Sinkronizacija tax promjena s guestTaxConfig za PRAVE goste
// SAMO LOKALNO - bez spremanja u bazu!
// ============================================================================
const syncTaxChangeToGuestConfig = (
    visaInfoId: number,
    field: 'fixedTaxId' | 'fixedTaxId2',
    value: number | null,
    sourceType: 'tax' | 'posItem' | null,
    taxes: TaxModel[], // NOVO
    defaultPosItems: DefaultPosItem[], // NOVO
) => {
    const freshReservation = ctrl.getActiveReservation();
    const currentConfig = parseGuestTaxConfig(freshReservation);

    if (!currentConfig) return;

    // NOVO: Dohvati cijenu i ime
    const taxInfo = getTaxAmountInfoLocal(value, sourceType, taxes, defaultPosItems);

    const newConfig: GuestTaxConfig = {
        adults: currentConfig.adults.map((a) => ({ ...a })),
        children: currentConfig.children.map((c) => ({ ...c })),
    };

    // Pronađi slot po visaInfoId u adults
    let adultIdx = newConfig.adults.findIndex((a) => a.visaInfoId === visaInfoId);

    if (adultIdx === -1) {
        adultIdx = newConfig.adults.findIndex((a) => a.visaInfoId === null || a.visaInfoId === undefined);
        if (adultIdx !== -1) {
            newConfig.adults[adultIdx].visaInfoId = visaInfoId;
        }
    }

    if (adultIdx !== -1) {
        newConfig.adults[adultIdx] = {
            ...newConfig.adults[adultIdx],
            [field]: value,
            fixedTaxType: sourceType,
            // NOVO: Dodaj cijenu i ime
            ...(field === 'fixedTaxId'
                ? {
                      fixedTaxAmount1: taxInfo.amount,
                      fixedTaxName1: taxInfo.name,
                  }
                : {
                      fixedTaxAmount2: taxInfo.amount,
                      fixedTaxName2: taxInfo.name,
                  }),
        };
    }

    // Pronađi slot po visaInfoId u children
    let childIdx = newConfig.children.findIndex((c) => c.visaInfoId === visaInfoId);

    if (childIdx === -1 && adultIdx === -1) {
        childIdx = newConfig.children.findIndex((c) => c.visaInfoId === null || c.visaInfoId === undefined);
        if (childIdx !== -1) {
            newConfig.children[childIdx].visaInfoId = visaInfoId;
        }
    }

    if (childIdx !== -1) {
        newConfig.children[childIdx] = {
            ...newConfig.children[childIdx],
            [field]: value,
            fixedTaxType: sourceType,
            // NOVO: Dodaj cijenu i ime
            ...(field === 'fixedTaxId'
                ? {
                      fixedTaxAmount1: taxInfo.amount,
                      fixedTaxName1: taxInfo.name,
                  }
                : {
                      fixedTaxAmount2: taxInfo.amount,
                      fixedTaxName2: taxInfo.name,
                  }),
        };
    }

    const updatedRes = { ...freshReservation };
    updatedRes.guestTaxConfig = newConfig;
    ctrl.updateActiveReservation(updatedRes);
};
// ============================================================================
// HELPER: Sinkronizacija datuma s guestTaxConfig za PRAVE goste
// SAMO LOKALNO - bez spremanja u bazu!
// ============================================================================
const syncDateChangeToGuestConfig = (
    visaInfoId: number,
    field: 'checkInTimestamp' | 'checkOutTimestamp',
    value: number | null,
) => {
    const freshReservation = ctrl.getActiveReservation();
    const currentConfig = parseGuestTaxConfig(freshReservation);

    if (!currentConfig) return;

    const newConfig: GuestTaxConfig = {
        adults: currentConfig.adults.map((a) => ({ ...a })),
        children: currentConfig.children.map((c) => ({ ...c })),
    };

    // Pronađi slot po visaInfoId u adults
    let adultIdx = newConfig.adults.findIndex((a) => a.visaInfoId === visaInfoId);

    // NOVO: Ako nema, linkaj u prvi prazan slot
    if (adultIdx === -1) {
        adultIdx = newConfig.adults.findIndex((a) => a.visaInfoId === null || a.visaInfoId === undefined);
        if (adultIdx !== -1) {
            newConfig.adults[adultIdx].visaInfoId = visaInfoId;
        }
    }

    if (adultIdx !== -1) {
        newConfig.adults[adultIdx] = {
            ...newConfig.adults[adultIdx],
            [field]: value,
        };
    }

    // Pronađi slot po visaInfoId u children
    let childIdx = newConfig.children.findIndex((c) => c.visaInfoId === visaInfoId);

    // NOVO: Ako nema, linkaj u prvi prazan slot
    if (childIdx === -1 && adultIdx === -1) {
        childIdx = newConfig.children.findIndex((c) => c.visaInfoId === null || c.visaInfoId === undefined);
        if (childIdx !== -1) {
            newConfig.children[childIdx].visaInfoId = visaInfoId;
        }
    }

    if (childIdx !== -1) {
        newConfig.children[childIdx] = {
            ...newConfig.children[childIdx],
            [field]: value,
        };
    }

    const updatedRes = { ...freshReservation };
    updatedRes.guestTaxConfig = newConfig;
    ctrl.updateActiveReservation(updatedRes);
};
// ============================================================================
// GUEST STAY MODAL CONTENT - za PRAVE goste (s visaInfoId)
// AŽURIRANO: Bez auto-spremanja u bazu!
// ============================================================================
interface GuestStayModalContentProps {
    customerId: number;
    visaInfoId: number;
    guestName: string;
    resCheckIn: number;
    resCheckOut: number;
}

const GuestStayModalContent = (props: GuestStayModalContentProps) => {
    const { customerId, visaInfoId, guestName, resCheckIn, resCheckOut } = props;

    const [checkInValue, setCheckInValue] = React.useState<number | null>(null);
    const [checkOutValue, setCheckOutValue] = React.useState<number | null>(null);
    const [hasCustomDates, setHasCustomDates] = React.useState(false);

    const getFreshVisaData = () => {
        const freshReservation = ctrl.getActiveReservation();
        return freshReservation.VisaInfo?.find((v) => v.customerId === customerId);
    };

    // Provjeri da bar jedan DRUGI odrasli ima puni boravak (checkIn <= res.checkIn, checkOut >= res.checkOut)
    const anotherAdultHasFullCoverage = (newCheckIn: number, newCheckOut: number): boolean => {
        const freshRes = ctrl.getActiveReservation();
        const isShorter = newCheckIn > resCheckIn || newCheckOut < resCheckOut;
        if (!isShorter) return true;
        const config = parseGuestTaxConfig(freshRes);
        return (config?.adults || [])
            .filter((a) => a.visaInfoId !== visaInfoId)
            .some(
                (a) =>
                    (!a.checkInTimestamp || a.checkInTimestamp <= resCheckIn) &&
                    (!a.checkOutTimestamp || a.checkOutTimestamp >= resCheckOut)
            );
    };

    React.useEffect(() => {
        const freshReservation = ctrl.getActiveReservation();
        const visaData = getFreshVisaData();

        // Dohvati datume iz guestTaxConfig (primarni izvor)
        const guestConfig = parseGuestTaxConfig(freshReservation);
        let configCheckIn: number | null = null;
        let configCheckOut: number | null = null;

        if (guestConfig && visaData?.id) {
            const adultConfig = guestConfig.adults.find((a) => a.visaInfoId === visaData.id);
            const childConfig = guestConfig.children.find((c) => c.visaInfoId === visaData.id);
            const config = adultConfig || childConfig;

            if (config) {
                configCheckIn = config.checkInTimestamp || null;
                configCheckOut = config.checkOutTimestamp || null;
            }
        }

        // Fallback na visaData pa na reservation datume
        const checkIn = configCheckIn || visaData?.checkInTimestamp || resCheckIn;
        const checkOut = configCheckOut || visaData?.checkOutTimestamp || resCheckOut;
        setCheckInValue(checkIn);
        setCheckOutValue(checkOut);

        // Provjeri da li ima custom datume (različiti od rezervacijskih)
        const isCustom = checkIn && checkOut && (checkIn !== resCheckIn || checkOut !== resCheckOut);
        setHasCustomDates(!!isCustom);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, resCheckIn, resCheckOut]);

    const handleReset = () => {
        // SAMO LOKALNO - sinkroniziraj s guestTaxConfig
        syncDateChangeToGuestConfig(visaInfoId, 'checkInTimestamp', null);
        syncDateChangeToGuestConfig(visaInfoId, 'checkOutTimestamp', null);

        // Update local state
        setCheckInValue(resCheckIn);
        setCheckOutValue(resCheckOut);
        setHasCustomDates(false);

        toast(translate('Dates reset to reservation dates'), {
            position: toast.POSITION.BOTTOM_RIGHT,
            type: toast.TYPE.SUCCESS,
        });
    };

    const handleCheckInChange = (selectedDate: Date | number) => {
        const visaData = getFreshVisaData();

        if (!visaData) {
            console.log('NO VISA DATA - RETURNING');
            return;
        }

        let newTimestamp: number;
        if (selectedDate instanceof Date) {
            newTimestamp = selectedDate.getTime();
        } else {
            newTimestamp = selectedDate;
        }

        const finalValue = dayjs(newTimestamp).startOf('day').valueOf();

        // Validacija
        const resCheckInStart = dayjs(resCheckIn).startOf('day').valueOf();
        const resCheckOutEnd = dayjs(resCheckOut).endOf('day').valueOf();
        const currentCheckOut = checkOutValue || resCheckOut;

        if (finalValue < resCheckInStart) {
            toast(translate('Check in cannot be before reservation start!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue > resCheckOutEnd) {
            toast(translate('Check in cannot be after reservation end!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue > currentCheckOut) {
            toast(translate('Check in cannot be after check out!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (!anotherAdultHasFullCoverage(finalValue, checkOutValue || resCheckOut)) {
            toast(translate('At least one adult must cover the full reservation period'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.WARNING,
            });
            return;
        }

        // SAMO LOKALNO - sinkroniziraj s guestTaxConfig
        syncDateChangeToGuestConfig(visaInfoId, 'checkInTimestamp', finalValue);
        setCheckInValue(finalValue);
        setHasCustomDates(true);
    };

    const handleCheckOutChange = (selectedDate: Date | number) => {
        const visaData = getFreshVisaData();

        if (!visaData) {
            console.log('NO VISA DATA - RETURNING');
            return;
        }

        let newTimestamp: number;
        if (selectedDate instanceof Date) {
            newTimestamp = selectedDate.getTime();
        } else {
            newTimestamp = selectedDate;
        }

        const finalValue = dayjs(newTimestamp).endOf('day').valueOf();

        // Validacija
        const resCheckInStart = dayjs(resCheckIn).startOf('day').valueOf();
        const resCheckOutEnd = dayjs(resCheckOut).endOf('day').valueOf();
        const currentCheckIn = checkInValue || resCheckIn;

        if (finalValue < resCheckInStart) {
            toast(translate('Check out cannot be before reservation start!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue > resCheckOutEnd) {
            toast(translate('Check out cannot be after reservation end!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue < currentCheckIn) {
            toast(translate('Check out cannot be before check in!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (!anotherAdultHasFullCoverage(checkInValue || resCheckIn, finalValue)) {
            toast(translate('At least one adult must cover the full reservation period'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.WARNING,
            });
            return;
        }

        // SAMO LOKALNO - sinkroniziraj s guestTaxConfig
        syncDateChangeToGuestConfig(visaInfoId, 'checkOutTimestamp', finalValue);
        setCheckOutValue(finalValue);
        setHasCustomDates(true);
    };

    return (
        <div className="justify-content-center">
            <div className="mb-16p">
                {translate('Select check in and check out date for guest')} {guestName}
            </div>
            <div className="display-flex justify-content-center">
                <div>
                    <div className="text-center mb-4p" style={{ fontSize: '12px', color: '#666' }}>
                        {translate('Check in')}
                    </div>
                    <CustomDayPicker value={checkInValue || resCheckIn} onSelect={handleCheckInChange} />
                </div>

                <span className="ml-8p">
                    <div>
                        <div className="text-center mb-4p" style={{ fontSize: '12px', color: '#666' }}>
                            {translate('Check out')}
                        </div>
                        <CustomDayPicker value={checkOutValue || resCheckOut} onSelect={handleCheckOutChange} />
                    </div>
                </span>
            </div>

            <div className="mt-12p text-center" style={{ fontSize: '12px', color: '#888' }}>
                {checkInValue && checkOutValue && (
                    <>
                        {dayjs(checkInValue).format('DD.MM.YYYY')} - {dayjs(checkOutValue).format('DD.MM.YYYY')} (
                        {dayjs(checkOutValue).diff(dayjs(checkInValue), 'day')} {translate('nights')})
                    </>
                )}
            </div>

            {hasCustomDates && (
                <div className="mt-16p text-center">
                    <button className="button-light" onClick={handleReset} style={{ fontSize: '12px' }}>
                        <i className="fa fa-undo mr-4p" />
                        {translate('Reset to reservation dates')}
                    </button>
                </div>
            )}
        </div>
    );
};

// ============================================================================
// VIRTUAL GUEST STAY MODAL - za VIRTUALNE goste (bez visaInfoId)
// ============================================================================
interface VirtualGuestStayModalContentProps {
    guestType: 'adult' | 'child';
    configIndex: number;
    guestLabel: string;
    currentCheckIn: number | null;
    currentCheckOut: number | null;
    resCheckIn: number;
    resCheckOut: number;
    onSave: (checkIn: number, checkOut: number) => void;
}

const VirtualGuestStayModalContent = (props: VirtualGuestStayModalContentProps) => {
    const { guestLabel, currentCheckIn, currentCheckOut, resCheckIn, resCheckOut, onSave } = props;

    const [checkInValue, setCheckInValue] = React.useState<number>(currentCheckIn || resCheckIn);
    const [checkOutValue, setCheckOutValue] = React.useState<number>(currentCheckOut || resCheckOut);

    const hasCustomDates =
        currentCheckIn && currentCheckOut && (currentCheckIn !== resCheckIn || currentCheckOut !== resCheckOut);

    const handleCheckInChange = (selectedDate: Date | number) => {
        let newTimestamp: number;
        if (selectedDate instanceof Date) {
            newTimestamp = selectedDate.getTime();
        } else {
            newTimestamp = selectedDate;
        }

        const finalValue = dayjs(newTimestamp).startOf('day').valueOf();

        const resCheckInStart = dayjs(resCheckIn).startOf('day').valueOf();
        const resCheckOutEnd = dayjs(resCheckOut).endOf('day').valueOf();

        if (finalValue < resCheckInStart || finalValue > resCheckOutEnd) {
            toast(translate('Date must be within reservation period!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue > checkOutValue) {
            toast(translate('Check in cannot be after check out!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        setCheckInValue(finalValue);
    };

    const handleCheckOutChange = (selectedDate: Date | number) => {
        let newTimestamp: number;
        if (selectedDate instanceof Date) {
            newTimestamp = selectedDate.getTime();
        } else {
            newTimestamp = selectedDate;
        }

        const finalValue = dayjs(newTimestamp).endOf('day').valueOf();

        const resCheckInStart = dayjs(resCheckIn).startOf('day').valueOf();
        const resCheckOutEnd = dayjs(resCheckOut).endOf('day').valueOf();

        if (finalValue < resCheckInStart || finalValue > resCheckOutEnd) {
            toast(translate('Date must be within reservation period!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        if (finalValue < checkInValue) {
            toast(translate('Check out cannot be before check in!'), {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
            return;
        }

        setCheckOutValue(finalValue);
    };

    const handleConfirm = () => {
        onSave(checkInValue, checkOutValue);
        closeModal();
    };

    const handleReset = () => {
        setCheckInValue(resCheckIn);
        setCheckOutValue(resCheckOut);
    };

    return (
        <div className="justify-content-center">
            <div className="mb-16p">
                {translate('Select check in and check out date for')} {guestLabel}
            </div>
            <div className="display-flex justify-content-center">
                <div>
                    <div className="text-center mb-4p" style={{ fontSize: '12px', color: '#666' }}>
                        {translate('Check in')}
                    </div>
                    <CustomDayPicker value={checkInValue} onSelect={handleCheckInChange} />
                </div>

                <span className="ml-8p">
                    <div>
                        <div className="text-center mb-4p" style={{ fontSize: '12px', color: '#666' }}>
                            {translate('Check out')}
                        </div>
                        <CustomDayPicker value={checkOutValue} onSelect={handleCheckOutChange} />
                    </div>
                </span>
            </div>

            <div className="mt-12p text-center" style={{ fontSize: '12px', color: '#888' }}>
                {dayjs(checkInValue).format('DD.MM.YYYY')} - {dayjs(checkOutValue).format('DD.MM.YYYY')} (
                {dayjs(checkOutValue).diff(dayjs(checkInValue), 'day')} {translate('nights')})
            </div>

            <div className="mt-16p text-center" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                {hasCustomDates && (
                    <button className="button-light" onClick={handleReset} style={{ fontSize: '12px' }}>
                        <i className="fa fa-undo mr-4p" />
                        {translate('Reset')}
                    </button>
                )}
                <button className="button-primary" onClick={handleConfirm}>
                    {translate('Confirm')}
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
interface VisaInfoProps {
    reservation: Reservation;
    history: any;
    otherSettings: any;
}

interface CustomerInfoProps {
    editCustomer: any;
    visaDataModal: any;
    showDeleteModal: any;
    customer: CustomerModel;
    invalidVisaIds: number[];
    index: number;
    reservationHolderId: number;
    reservation: Reservation;
    contextMenuIndex: number | null;
    setContextMenuIndex: Function;
    points: any;
    setPoints: Function;
    location?: any;
    otherSettings: any;
    taxes: TaxModel[];
    defaultPosItems: DefaultPosItem[];
}

const VisaInfo = (props: VisaInfoProps) => {
    const { reservation, history } = props;
    const [contextMenuIndex, setContextMenuIndex] = React.useState<number | null>(null);
    const [points, setPoints] = React.useState<any>(null);
    const [activeChildSearchIndex, setActiveChildSearchIndex] = React.useState<number | null>(null);
    const [activeAdultSearchIndex, setActiveAdultSearchIndex] = React.useState<number | null>(null);

    const searchContainerRef = React.useRef<HTMLDivElement>(null);

    const taxes: TaxModel[] = useStore.getState().model.Tax || [];
    const invoicingSettings = useStore.getState().invoicingSettings;
    const defaultPosItems: DefaultPosItem[] = invoicingSettings?.defaultPosItems || [];

    // Click outside handler
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                (activeChildSearchIndex !== null || activeAdultSearchIndex !== null) &&
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target as Node)
            ) {
                const target = event.target as HTMLElement;
                const isDropdownClick =
                    target.closest('.customer-search-dropdown') ||
                    target.closest('.search-results') ||
                    target.closest('[class*="dropdown"]');

                if (!isDropdownClick) {
                    setActiveChildSearchIndex(null);
                    setActiveAdultSearchIndex(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeChildSearchIndex, activeAdultSearchIndex]);

    // Auto-focus
    React.useEffect(() => {
        if (activeAdultSearchIndex !== null) {
            setTimeout(() => {
                const container = document.querySelector(`[data-adult-search="${activeAdultSearchIndex}"]`);
                const input = container?.querySelector('input') as HTMLInputElement;
                if (input) input.focus();
            }, 50);
        }
    }, [activeAdultSearchIndex]);

    React.useEffect(() => {
        if (activeChildSearchIndex !== null) {
            setTimeout(() => {
                const container = document.querySelector(`[data-child-search="${activeChildSearchIndex}"]`);
                const input = container?.querySelector('input') as HTMLInputElement;
                if (input) input.focus();
            }, 50);
        }
    }, [activeChildSearchIndex]);

    if ((reservation.customerId === null || reservation.companyId === null) && reservation.id === null) {
        return null;
    }
    const { location } = history;

    const childrenCount = reservation.childrenNumber || 0;
    const actualAdultsCount = reservation.adultsNumber || 1;

    const guestTaxConfig = parseGuestTaxConfig(reservation);

    // Dropdown opcije
    const adultTaxOptions = getDropdownOptions(taxes, defaultPosItems, 'tax', 'adult');
    const adultInsuranceOptions = getDropdownOptions(taxes, defaultPosItems, 'insurance', 'adult');
    const childTaxOptions = getDropdownOptions(taxes, defaultPosItems, 'tax', 'child');
    const childInsuranceOptions = getDropdownOptions(taxes, defaultPosItems, 'insurance', 'child');

    const allTaxOptions = [...adultTaxOptions, ...childTaxOptions].filter(
        (opt, index, self) => index === self.findIndex((o) => o.id === opt.id && o.sourceType === opt.sourceType),
    );

    const allInsuranceOptions = [...adultInsuranceOptions, ...childInsuranceOptions].filter(
        (opt, index, self) => index === self.findIndex((o) => o.id === opt.id && o.sourceType === opt.sourceType),
    );

    // ========================================================================
    // HELPERS
    // ========================================================================

    // ========================================================================
    // BULLETPROOF HELPER: Provjeri da li je visaInfoId VALIDAN (gost još postoji)
    // ========================================================================
    const isVisaInfoIdValid = (visaInfoId: number | null | undefined): boolean => {
        if (visaInfoId === null || visaInfoId === undefined) return false;
        // Provjeri da li taj visaInfo još postoji u reservation.VisaInfo
        return (reservation.VisaInfo || []).some((v) => v.id === visaInfoId);
    };

    // ========================================================================
    // BULLETPROOF: Pronađi PRVI STVARNO PRAZAN adult slot
    // Vraća indeks prvog slota gdje visaInfoId je null/undefined ILI više ne postoji
    // ========================================================================
    const findFirstFreeAdultSlot = (): number | null => {
        const adultsConfigs = guestTaxConfig?.adults || [];

        for (let i = 0; i < actualAdultsCount; i++) {
            const config = adultsConfigs[i];
            // Slot je PRAZAN ako nema config, nema visaInfoId, ili visaInfoId više nije validan
            if (!config || !isVisaInfoIdValid(config.visaInfoId)) {
                return i;
            }
        }

        // Svi slotovi su popunjeni
        return null;
    };

    // ========================================================================
    // BULLETPROOF: Pronađi najbolji STVARNO PRAZAN child slot po dobi
    // ========================================================================
    const findBestChildSlotByAge = (customerAge: number): number | null => {
        const childrenConfigs = guestTaxConfig?.children || [];

        // Prvo pronađi sve PRAZNE slotove
        const emptySlotIndices: number[] = [];
        for (let i = 0; i < childrenCount; i++) {
            const config = childrenConfigs[i];
            if (!config || !isVisaInfoIdValid(config.visaInfoId)) {
                emptySlotIndices.push(i);
            }
        }

        // Ako nema praznih slotova, vrati null
        if (emptySlotIndices.length === 0) {
            return null;
        }

        // Pronađi najbolji prazan slot po dobi
        let bestSlotIndex: number | null = null;
        let smallestAgeDiff = Infinity;

        for (const slotIndex of emptySlotIndices) {
            const config = childrenConfigs[slotIndex];
            const slotAge = config?.age ?? -1;

            if (slotAge < 0) {
                // Slot bez definirane dobi - uzmi ga ako nemamo bolji
                if (bestSlotIndex === null) {
                    bestSlotIndex = slotIndex;
                }
            } else {
                const ageDiff = Math.abs(slotAge - customerAge);
                if (ageDiff < smallestAgeDiff) {
                    smallestAgeDiff = ageDiff;
                    bestSlotIndex = slotIndex;
                }
            }
        }

        return bestSlotIndex !== null ? bestSlotIndex : emptySlotIndices[0];
    };

    // ========================================================================
    // HANDLERS
    // ========================================================================

    const editCustomer = (customer: CustomerModel) => {
        setModalContent(
            <CustomerForm
                customerId={customer.id}
                callback={async (updatedCustomerData: CustomerModel) => {
                    if (reservation && reservation.id) {
                        const guestReference = reservation.VisaInfo?.find((v) => v.customerId === customer.id);
                        if (guestReference && updatedCustomerData.id) {
                            const ref = { ...guestReference };
                            ref.customerId = updatedCustomerData.id;
                            await ctrl.updateReservationGuestReference(ref);
                        }
                    }
                }}
                cancelHandler={() => closeModal()}
                reservation={reservation}
                customHeader={''}
                showPageTitle={false}
            />,
            translate('Edit customer'),
            false,
            'modal-lg',
        );
    };

    const createNewCustomerForChild = (childIndex: number) => {
        setModalContent(
            <CustomerForm
                customerId={null}
                callback={async (newCustomer: CustomerModel) => {
                    if (newCustomer && newCustomer.id) {
                        await addGuestToChildSlot(newCustomer, childIndex);
                        closeModal();
                    }
                }}
                cancelHandler={() => closeModal()}
                reservation={reservation}
                customHeader={''}
                showPageTitle={false}
            />,
            translate('New customer'),
            false,
            'modal-lg',
        );
    };

    const createNewCustomerForAdult = (adultIndex: number) => {
        setModalContent(
            <CustomerForm
                customerId={null}
                callback={async (newCustomer: CustomerModel) => {
                    if (newCustomer && newCustomer.id) {
                        await addGuestToAdultSlot(newCustomer, adultIndex);
                        closeModal();
                    }
                }}
                cancelHandler={() => closeModal()}
                reservation={reservation}
                customHeader={''}
                showPageTitle={false}
            />,
            translate('New customer'),
            false,
            'modal-lg',
        );
    };

    const updateCustomerModal = (
        customer: CustomerModel,
        scannedCustomer: CustomerModel,
        updatedGuestReference: ReservationGuestReferenceModel,
    ) => {
        setModalContent(
            <div className="container-fluid">
                <div className="text-center">
                    {translate('Do you want to update the customer') +
                        ' ' +
                        customer.firstName +
                        ' ' +
                        customer.lastName +
                        ' ' +
                        translate('with new data?')}
                    <hr />
                </div>
                <div className="text-center">
                    <ConfirmButtons
                        onConfirm={async () => {
                            try {
                                await ctrl.updateCustomer(scannedCustomer);
                                await ctrl.updateReservationGuestReference(updatedGuestReference);
                                closeModal();
                            } catch (error) {
                                closeModal();
                            }
                        }}
                        onCancel={async () => {
                            await ctrl.updateReservationGuestReference(updatedGuestReference);
                            closeModal();
                        }}
                    />
                </div>
            </div>,
            translate('Update customer'),
            false,
            'modal-md',
        );
    };

    const visaDataModal = (customer: CustomerModel) => {
        let person = `${customer.firstName ? customer.firstName : ''} ${customer.lastName ? customer.lastName : ''}`;
        person = person.toUpperCase();
        const visaData = reservation.VisaInfo?.find((v) => v.customerId === customer.id);

        setModalContent(
            <VisaDataModal
                customer={customer}
                reservation={reservation}
                visaData={visaData}
                saveHandler={async (
                    updatedGuestReference: ReservationGuestReferenceModel,
                    scannedCustomer: CustomerModel | any,
                    update: boolean,
                ) => {
                    try {
                        if (update) {
                            const newCustomerData = { ...customer } as CustomerModel | any;
                            newCustomerData.documentId = updatedGuestReference.documentId;
                            newCustomerData.documentType = updatedGuestReference.documentType;
                            scannedCustomer &&
                                Object.keys(scannedCustomer).forEach((c: string) => {
                                    if (c === 'id' || c === 'documentId') {
                                    } else {
                                        newCustomerData[c] = scannedCustomer[c];
                                    }
                                });
                            updateCustomerModal(customer, newCustomerData, updatedGuestReference);
                        } else {
                            await ctrl.updateReservationGuestReference(updatedGuestReference);
                            closeModal();
                        }
                    } catch (error: any) {
                        closeModal();
                    }
                }}
            />,
            translate('VISA AND DOCUMENT DATA FOR') + ' ' + person,
            false,
            'modal-lg',
        );
    };

    const showDeleteModal = (customer: CustomerModel) => {
        setModalContent(
            <Delete
                actionForYes={async () => {
                    try {
                        if (reservation?.statusEnum !== ReservationStatus.closed) {
                            const resGuestRef = reservation.VisaInfo?.find((r) => r.customerId === customer.id);
                            if (reservation.customerId !== customer.id && resGuestRef) {
                                if (resGuestRef.id) {
                                    await ctrl.removeGuestFromReservation(resGuestRef.id);
                                    await saveOrUpdateReservation(ctrl.getActiveReservation());
                                }
                            }
                        } else {
                            toast(translate('Reservation closed!'), {
                                position: toast.POSITION.TOP_RIGHT,
                                type: toast.TYPE.ERROR,
                            });
                        }
                    } catch (error: any) {
                        console.error(error);
                    }
                }}
            />,
            translate('Delete') + ' ' + translate('reservation guest'),
            false,
            'modal-md',
        );
    };

    // ========================================================================
    // CHILD HANDLERS
    // ========================================================================

    const handleChildAgeChange = (childIndex: number, newAge: number) => {
        const currentConfig = guestTaxConfig || { adults: [], children: [] };
        const newConfig: GuestTaxConfig = {
            adults: currentConfig.adults.map((a) => ({ ...a })),
            children: currentConfig.children.map((c) => ({ ...c })),
        };

        while (newConfig.children.length <= childIndex) {
            const defaultTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
            const defaultInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');

            // NOVO: Dohvati cijene
            const taxInfo1 = getTaxAmountInfoLocal(defaultTax.id, defaultTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                defaultInsurance.id,
                defaultInsurance.sourceType,
                taxes,
                defaultPosItems,
            );

            newConfig.children.push({
                age: -1,
                visaInfoId: null,
                fixedTaxId: defaultTax.id,
                fixedTaxId2: defaultInsurance.id,
                fixedTaxType: defaultTax.sourceType,
                fixedTaxAmount1: taxInfo1.amount, // NOVO
                fixedTaxAmount2: taxInfo2.amount, // NOVO
                fixedTaxName1: taxInfo1.name, // NOVO
                fixedTaxName2: taxInfo2.name, // NOVO
                checkInTimestamp: reservation.checkInTimestamp,
                checkOutTimestamp: reservation.checkOutTimestamp,
            });
        }

        const suggestedTax = getSuggestedItemByAge(newAge >= 0 ? newAge : 5, 'tax', taxes, defaultPosItems, 'child');
        const suggestedInsurance = getSuggestedItemByAge(
            newAge >= 0 ? newAge : 5,
            'insurance',
            taxes,
            defaultPosItems,
            'child',
        );

        // NOVO: Dohvati cijene
        const taxInfo1 = getTaxAmountInfoLocal(suggestedTax.id, suggestedTax.sourceType, taxes, defaultPosItems);
        const taxInfo2 = getTaxAmountInfoLocal(
            suggestedInsurance.id,
            suggestedInsurance.sourceType,
            taxes,
            defaultPosItems,
        );

        newConfig.children[childIndex] = {
            ...newConfig.children[childIndex],
            age: newAge,
            fixedTaxId: suggestedTax.id,
            fixedTaxId2: suggestedInsurance.id,
            fixedTaxType: suggestedTax.sourceType,
            fixedTaxAmount1: taxInfo1.amount, // NOVO
            fixedTaxAmount2: taxInfo2.amount, // NOVO
            fixedTaxName1: taxInfo1.name, // NOVO
            fixedTaxName2: taxInfo2.name, // NOVO
        };

        const updatedReservation = { ...reservation };
        updatedReservation.guestTaxConfig = newConfig;
        ctrl.updateActiveReservation(updatedReservation);
    };

    const handleChildTaxChange = (childIndex: number, field: 'fixedTaxId' | 'fixedTaxId2', value: string) => {
        const parsed = parseDropdownValue(value);
        const currentConfig = guestTaxConfig || { adults: [], children: [] };
        const newConfig: GuestTaxConfig = {
            adults: currentConfig.adults.map((a) => ({ ...a })),
            children: currentConfig.children.map((c) => ({ ...c })),
        };

        while (newConfig.children.length <= childIndex) {
            const defaultTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
            const defaultInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
            const taxInfo1 = getTaxAmountInfoLocal(defaultTax.id, defaultTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                defaultInsurance.id,
                defaultInsurance.sourceType,
                taxes,
                defaultPosItems,
            );

            newConfig.children.push({
                age: -1,
                visaInfoId: null,
                fixedTaxId: defaultTax.id,
                fixedTaxId2: defaultInsurance.id,
                fixedTaxType: defaultTax.sourceType,
                fixedTaxAmount1: taxInfo1.amount,
                fixedTaxAmount2: taxInfo2.amount,
                fixedTaxName1: taxInfo1.name,
                fixedTaxName2: taxInfo2.name,
                checkInTimestamp: reservation.checkInTimestamp,
                checkOutTimestamp: reservation.checkOutTimestamp,
            });
        }

        // NOVO: Dohvati cijenu za novu vrijednost
        const taxInfo = getTaxAmountInfoLocal(parsed.id, parsed.sourceType, taxes, defaultPosItems);

        newConfig.children[childIndex] = {
            ...newConfig.children[childIndex],
            [field]: parsed.id,
            fixedTaxType: parsed.sourceType,
            // NOVO: Dodaj cijenu i ime ovisno o polju
            ...(field === 'fixedTaxId'
                ? {
                      fixedTaxAmount1: taxInfo.amount,
                      fixedTaxName1: taxInfo.name,
                  }
                : {
                      fixedTaxAmount2: taxInfo.amount,
                      fixedTaxName2: taxInfo.name,
                  }),
        };

        const updatedReservation = { ...reservation };
        updatedReservation.guestTaxConfig = newConfig;
        ctrl.updateActiveReservation(updatedReservation);
    };

    const handleChildDateSave = (childIndex: number, checkIn: number, checkOut: number) => {
        const currentConfig = guestTaxConfig || { adults: [], children: [] };
        const newConfig: GuestTaxConfig = {
            adults: currentConfig.adults.map((a) => ({ ...a })),
            children: currentConfig.children.map((c) => ({ ...c })),
        };

        while (newConfig.children.length <= childIndex) {
            const defaultTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
            const defaultInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
            const taxInfo1 = getTaxAmountInfoLocal(defaultTax.id, defaultTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                defaultInsurance.id,
                defaultInsurance.sourceType,
                taxes,
                defaultPosItems,
            );
            newConfig.children.push({
                age: -1,
                visaInfoId: null,
                fixedTaxId: defaultTax.id,
                fixedTaxId2: defaultInsurance.id,
                fixedTaxType: defaultTax.sourceType,
                checkInTimestamp: reservation.checkInTimestamp,
                checkOutTimestamp: reservation.checkOutTimestamp,
                fixedTaxAmount1: taxInfo1.amount,
                fixedTaxAmount2: taxInfo2.amount,
                fixedTaxName1: taxInfo1.name,
                fixedTaxName2: taxInfo2.name,
            });
        }

        newConfig.children[childIndex] = {
            ...newConfig.children[childIndex],
            checkInTimestamp: checkIn,
            checkOutTimestamp: checkOut,
        };

        const updatedReservation = { ...reservation };
        updatedReservation.guestTaxConfig = newConfig;
        ctrl.updateActiveReservation(updatedReservation);
    };

    // ========================================================================
    // ADULT HANDLERS
    // ========================================================================

    const handleAdultTaxChange = (adultIndex: number, field: 'fixedTaxId' | 'fixedTaxId2', value: string) => {
        const parsed = parseDropdownValue(value);
        const currentConfig = guestTaxConfig || { adults: [], children: [] };
        const newConfig: GuestTaxConfig = {
            adults: currentConfig.adults.map((a) => ({ ...a })),
            children: currentConfig.children.map((c) => ({ ...c })),
        };

        while (newConfig.adults.length <= adultIndex) {
            const defaultTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
            const defaultInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
            const taxInfo1 = getTaxAmountInfoLocal(defaultTax.id, defaultTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                defaultInsurance.id,
                defaultInsurance.sourceType,
                taxes,
                defaultPosItems,
            );

            newConfig.adults.push({
                visaInfoId: null,
                fixedTaxId: defaultTax.id,
                fixedTaxId2: defaultInsurance.id,
                fixedTaxType: defaultTax.sourceType,
                fixedTaxAmount1: taxInfo1.amount,
                fixedTaxAmount2: taxInfo2.amount,
                fixedTaxName1: taxInfo1.name,
                fixedTaxName2: taxInfo2.name,
                checkInTimestamp: reservation.checkInTimestamp,
                checkOutTimestamp: reservation.checkOutTimestamp,
            });
        }

        // NOVO: Dohvati cijenu za novu vrijednost
        const taxInfo = getTaxAmountInfoLocal(parsed.id, parsed.sourceType, taxes, defaultPosItems);

        newConfig.adults[adultIndex] = {
            ...newConfig.adults[adultIndex],
            [field]: parsed.id,
            fixedTaxType: parsed.sourceType,
            ...(field === 'fixedTaxId'
                ? {
                      fixedTaxAmount1: taxInfo.amount,
                      fixedTaxName1: taxInfo.name,
                  }
                : {
                      fixedTaxAmount2: taxInfo.amount,
                      fixedTaxName2: taxInfo.name,
                  }),
        };

        const updatedReservation = { ...reservation };
        updatedReservation.guestTaxConfig = newConfig;
        ctrl.updateActiveReservation(updatedReservation);
    };

    const handleAdultDateSave = (adultIndex: number, checkIn: number, checkOut: number) => {
        const adultsNumber = reservation.adultsNumber || 1;

        if (adultsNumber > 1) {
            const isShorter =
                checkIn > reservation.checkInTimestamp ||
                checkOut < reservation.checkOutTimestamp;

            if (isShorter) {
                const currentAdults = (guestTaxConfig?.adults || []);
                const otherAdultHasFullCoverage = currentAdults
                    .filter((_, i) => i !== adultIndex)
                    .some(
                        (a) =>
                            (!a.checkInTimestamp || a.checkInTimestamp <= reservation.checkInTimestamp) &&
                            (!a.checkOutTimestamp || a.checkOutTimestamp >= reservation.checkOutTimestamp)
                    );

                if (!otherAdultHasFullCoverage) {
                    toast(translate('At least one adult must cover the full reservation period'), {
                        type: toast.TYPE.WARNING,
                        position: toast.POSITION.TOP_RIGHT,
                    });
                    return;
                }
            }
        }

        const currentConfig = guestTaxConfig || { adults: [], children: [] };
        const newConfig: GuestTaxConfig = {
            adults: currentConfig.adults.map((a) => ({ ...a })),
            children: currentConfig.children.map((c) => ({ ...c })),
        };

        while (newConfig.adults.length <= adultIndex) {
            const defaultTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
            const defaultInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
            newConfig.adults.push({
                visaInfoId: null,
                fixedTaxId: defaultTax.id,
                fixedTaxId2: defaultInsurance.id,
                fixedTaxType: defaultTax.sourceType,
                checkInTimestamp: reservation.checkInTimestamp,
                checkOutTimestamp: reservation.checkOutTimestamp,
            });
        }

        newConfig.adults[adultIndex] = {
            ...newConfig.adults[adultIndex],
            checkInTimestamp: checkIn,
            checkOutTimestamp: checkOut,
        };

        const updatedReservation = { ...reservation };
        updatedReservation.guestTaxConfig = newConfig;
        ctrl.updateActiveReservation(updatedReservation);
    };

    // ========================================================================
    // ADD GUEST TO SLOT
    // ========================================================================

    const addGuestToChildSlot = async (customer: CustomerModel, childIndex: number) => {
        try {
            if (reservation?.statusEnum === ReservationStatus.closed) {
                toast(translate('Reservation closed!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // BULLETPROOF CHECK 1: Provjera duplikata
            if (reservation.Guests?.some((g) => g.id === customer.id)) {
                toast(translate('Guest is already added to this reservation!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.WARNING,
                });
                return;
            }

            // BULLETPROOF CHECK 2: Provjera da li je KONKRETNI childIndex slot slobodan
            const childrenConfigs = guestTaxConfig?.children || [];
            const targetSlotConfig = childrenConfigs[childIndex];
            const isTargetSlotOccupied = targetSlotConfig && isVisaInfoIdValid(targetSlotConfig.visaInfoId);

            if (isTargetSlotOccupied) {
                toast(translate('This slot is already occupied!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // Dodatna provjera: da li uopće postoji taj slot (childIndex < childrenCount)
            if (childIndex >= childrenCount) {
                toast(translate('No free slots available! Increase adults or children count first.'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // BULLETPROOF CHECK 3: Warning za age mismatch (osoba >= 18 u CHILD slot)
            const customerDateOfBirthCheck = customer.dateOfBirth || null;
            let customerAgeCheck = 5;
            if (customerDateOfBirthCheck) {
                customerAgeCheck = Number(dayjs().diff(customerDateOfBirthCheck, 'years'));
            }
            if (customerAgeCheck >= 18) {
                toast(translate('Warning: Adding an adult (18+) to a child slot!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.WARNING,
                });
                // Nastavi - samo warning, ne blokiraj
            }

            const originalAdultsNumber = reservation.adultsNumber || 1;
            const originalChildrenNumber = reservation.childrenNumber || 0;

            const customerDateOfBirth = customer.dateOfBirth || null;
            let customerAge = 5;
            if (customerDateOfBirth) {
                customerAge = Number(dayjs().diff(customerDateOfBirth, 'years'));
            }

            const suggestedTax = getSuggestedItemByAge(customerAge, 'tax', taxes, defaultPosItems, 'child');
            const suggestedInsurance = getSuggestedItemByAge(customerAge, 'insurance', taxes, defaultPosItems, 'child');
            const taxInfo1 = getTaxAmountInfoLocal(suggestedTax.id, suggestedTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                suggestedInsurance.id,
                suggestedInsurance.sourceType,
                taxes,
                defaultPosItems,
            );

            const currentConfig = parseGuestTaxConfig(reservation) || { adults: [], children: [] };
            const newAdults = JSON.parse(JSON.stringify(currentConfig.adults));
            const newChildren = JSON.parse(JSON.stringify(currentConfig.children));

            while (newChildren.length <= childIndex) {
                const defaultTax = getSuggestedItemByAge(5, 'tax', taxes, defaultPosItems, 'child');
                const defaultInsurance = getSuggestedItemByAge(5, 'insurance', taxes, defaultPosItems, 'child');
                const defaultTaxInfo1 = getTaxAmountInfoLocal(
                    defaultTax.id,
                    defaultTax.sourceType,
                    taxes,
                    defaultPosItems,
                );
                const defaultTaxInfo2 = getTaxAmountInfoLocal(
                    defaultInsurance.id,
                    defaultInsurance.sourceType,
                    taxes,
                    defaultPosItems,
                );

                newChildren.push({
                    age: -1,
                    visaInfoId: null,
                    fixedTaxId: defaultTax.id,
                    fixedTaxId2: defaultInsurance.id,
                    fixedTaxType: defaultTax.sourceType,
                    fixedTaxAmount1: defaultTaxInfo1.amount,
                    fixedTaxAmount2: defaultTaxInfo2.amount,
                    fixedTaxName1: defaultTaxInfo1.name,
                    fixedTaxName2: defaultTaxInfo2.name,
                    checkInTimestamp: reservation.checkInTimestamp,
                    checkOutTimestamp: reservation.checkOutTimestamp,
                });
            }

            const existingConfig = newChildren[childIndex];
            const slotCheckIn = existingConfig?.checkInTimestamp || reservation.checkInTimestamp;
            const slotCheckOut = existingConfig?.checkOutTimestamp || reservation.checkOutTimestamp;

            await ctrl.addGuestToReservation(customer, suggestedTax.id, suggestedInsurance.id, suggestedTax.sourceType);

            const updatedRes = ctrl.getActiveReservation();
            const newVisaInfo = updatedRes.VisaInfo?.find((v) => v.customerId === customer.id);

            if (!newVisaInfo || !newVisaInfo.id) {
                console.error('addGuestToChildSlot: newVisaInfo not found or missing id!');
                setActiveChildSearchIndex(null);
                return;
            }

            newChildren[childIndex] = {
                ...newChildren[childIndex],
                visaInfoId: newVisaInfo.id,
                age: customerAge,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                fixedTaxAmount1: taxInfo1.amount,
                fixedTaxAmount2: taxInfo2.amount,
                fixedTaxName1: taxInfo1.name,
                fixedTaxName2: taxInfo2.name,
                checkInTimestamp: slotCheckIn,
                checkOutTimestamp: slotCheckOut,
            };

            const finalRes = { ...updatedRes };
            finalRes.adultsNumber = originalAdultsNumber;
            finalRes.childrenNumber = originalChildrenNumber;
            finalRes.guestTaxConfig = {
                adults: newAdults,
                children: newChildren,
            };

            ctrl.updateActiveReservation(finalRes);
            await saveOrUpdateReservation(ctrl.getActiveReservation());
            setActiveChildSearchIndex(null);
        } catch (error: any) {
            console.error('addGuestToChildSlot ERROR:', error);
            setActiveChildSearchIndex(null);
        }
    };

    const addGuestToAdultSlot = async (customer: CustomerModel, adultIndex: number, overrideAdultsCount?: number) => {
        try {
            if (reservation?.statusEnum === ReservationStatus.closed) {
                toast(translate('Reservation closed!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // BULLETPROOF CHECK 1: Provjera duplikata
            if (reservation.Guests?.some((g) => g.id === customer.id)) {
                toast(translate('Guest is already added to this reservation!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.WARNING,
                });
                return;
            }

            // BULLETPROOF CHECK 2: Provjera da li je KONKRETNI adultIndex slot slobodan
            const adultsConfigs = guestTaxConfig?.adults || [];
            const targetSlotConfig = adultsConfigs[adultIndex];
            const isTargetSlotOccupied = targetSlotConfig && isVisaInfoIdValid(targetSlotConfig.visaInfoId);

            if (isTargetSlotOccupied) {
                toast(translate('This slot is already occupied!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // Dodatna provjera: da li uopće postoji taj slot (adultIndex < actualAdultsCount)
            if (adultIndex >= (overrideAdultsCount ?? actualAdultsCount)) {
                toast(translate('No free slots available! Increase adults or children count first.'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // BULLETPROOF CHECK 3: Warning za age mismatch (osoba < 18 u ADULT slot)
            const customerDateOfBirthCheck = customer.dateOfBirth || null;
            let customerAgeCheck = 30;
            if (customerDateOfBirthCheck) {
                customerAgeCheck = Number(dayjs().diff(customerDateOfBirthCheck, 'years'));
            }
            if (customerAgeCheck < 18) {
                toast(translate('Warning: Adding a minor (under 18) to an adult slot!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.WARNING,
                });
                // Nastavi - samo warning, ne blokiraj
            }

            const originalAdultsNumber = reservation.adultsNumber || 1;
            const originalChildrenNumber = reservation.childrenNumber || 0;

            const customerDateOfBirth = customer.dateOfBirth || null;
            let customerAge = 30;
            if (customerDateOfBirth) {
                const calculatedAge = Number(dayjs().diff(customerDateOfBirth, 'years'));
                if (calculatedAge > 0) customerAge = calculatedAge;
            }

            const suggestedTax = getSuggestedItemByAge(customerAge, 'tax', taxes, defaultPosItems, 'adult');
            const suggestedInsurance = getSuggestedItemByAge(customerAge, 'insurance', taxes, defaultPosItems, 'adult');

            // NOVO: Dohvati cijene i imena
            const taxInfo1 = getTaxAmountInfoLocal(suggestedTax.id, suggestedTax.sourceType, taxes, defaultPosItems);
            const taxInfo2 = getTaxAmountInfoLocal(
                suggestedInsurance.id,
                suggestedInsurance.sourceType,
                taxes,
                defaultPosItems,
            );

            const currentConfig = parseGuestTaxConfig(reservation) || { adults: [], children: [] };
            const newAdults = JSON.parse(JSON.stringify(currentConfig.adults));
            const newChildren = JSON.parse(JSON.stringify(currentConfig.children));

            while (newAdults.length <= adultIndex) {
                const defaultTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
                const defaultInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');
                newAdults.push({
                    visaInfoId: null,
                    fixedTaxId: defaultTax.id,
                    fixedTaxId2: defaultInsurance.id,
                    fixedTaxType: defaultTax.sourceType,
                    checkInTimestamp: reservation.checkInTimestamp,
                    checkOutTimestamp: reservation.checkOutTimestamp,
                });
            }

            const existingConfig = newAdults[adultIndex];
            const slotCheckIn = existingConfig?.checkInTimestamp || reservation.checkInTimestamp;
            const slotCheckOut = existingConfig?.checkOutTimestamp || reservation.checkOutTimestamp;

            await ctrl.addGuestToReservation(customer, suggestedTax.id, suggestedInsurance.id, suggestedTax.sourceType);

            const updatedRes = ctrl.getActiveReservation();
            const newVisaInfo = updatedRes.VisaInfo?.find((v) => v.customerId === customer.id);

            if (!newVisaInfo || !newVisaInfo.id) {
                console.error('addGuestToAdultSlot: newVisaInfo not found or missing id!');
                setActiveAdultSearchIndex(null);
                return;
            }

            newAdults[adultIndex] = {
                ...newAdults[adultIndex],
                visaInfoId: newVisaInfo.id,
                fixedTaxId: suggestedTax.id,
                fixedTaxId2: suggestedInsurance.id,
                fixedTaxType: suggestedTax.sourceType,
                fixedTaxAmount1: taxInfo1.amount, // NOVO
                fixedTaxAmount2: taxInfo2.amount, // NOVO
                fixedTaxName1: taxInfo1.name, // NOVO
                fixedTaxName2: taxInfo2.name, // NOVO
                checkInTimestamp: slotCheckIn,
                checkOutTimestamp: slotCheckOut,
            };

            const finalRes = { ...updatedRes };
            finalRes.adultsNumber = overrideAdultsCount ?? originalAdultsNumber;
            finalRes.childrenNumber = originalChildrenNumber;
            finalRes.guestTaxConfig = {
                adults: newAdults,
                children: newChildren,
            };

            ctrl.updateActiveReservation(finalRes);
            await saveOrUpdateReservation(ctrl.getActiveReservation());
            setActiveAdultSearchIndex(null);
        } catch (error: any) {
            console.error('addGuestToAdultSlot ERROR:', error);
            setActiveAdultSearchIndex(null);
        }
    };

    const addGuestFromGeneralSearch = async (customer: CustomerModel) => {
        try {
            if (reservation?.statusEnum === ReservationStatus.closed) {
                toast(translate('Reservation closed!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.ERROR,
                });
                return;
            }

            // BULLETPROOF CHECK 1: Provjera duplikata
            if (reservation.Guests?.some((g) => g.id === customer.id)) {
                toast(translate('Guest is already added to this reservation!'), {
                    position: toast.POSITION.TOP_RIGHT,
                    type: toast.TYPE.WARNING,
                });
                return;
            }

            const customerDateOfBirth = customer.dateOfBirth || null;
            let customerAge = 30;

            if (customerDateOfBirth) {
                customerAge = Number(dayjs().diff(customerDateOfBirth, 'years'));
            }

            const isChild = customerAge < 18;

            const freeAdultSlot = findFirstFreeAdultSlot();
            const freeChildSlot = findBestChildSlotByAge(customerAge);

            // BULLETPROOF CHECK 2: Provjera kapaciteta - ako nema slobodnih slotova, povećaj adultsNumber
            if (freeAdultSlot === null && freeChildSlot === null) {
                const newAdultsNumber = actualAdultsCount + 1;
                await addGuestToAdultSlot(customer, actualAdultsCount, newAdultsNumber);
                return;
            }

            if (isChild) {
                if (freeChildSlot !== null) {
                    await addGuestToChildSlot(customer, freeChildSlot);
                } else if (freeAdultSlot !== null) {
                    // Warning će se prikazati unutar addGuestToAdultSlot
                    await addGuestToAdultSlot(customer, freeAdultSlot);
                }
                // OBRISANO: else blok koji je automatski povećavao childrenNumber
            } else {
                if (freeAdultSlot !== null) {
                    await addGuestToAdultSlot(customer, freeAdultSlot);
                } else if (freeChildSlot !== null) {
                    // Warning će se prikazati unutar addGuestToChildSlot
                    await addGuestToChildSlot(customer, freeChildSlot);
                }
                // OBRISANO: else blok koji je automatski povećavao adultsNumber
            }
        } catch (error: any) {
            console.error(error);
        }
    };

    // ========================================================================
    // GUEST LISTS
    // ========================================================================

    const adultVisaInfoIds = new Set(
        (guestTaxConfig?.adults || [])
            .map((a) => a.visaInfoId)
            .filter((id): id is number => id !== null && id !== undefined),
    );
    const childVisaInfoIds = new Set(
        (guestTaxConfig?.children || [])
            .map((c) => c.visaInfoId)
            .filter((id): id is number => id !== null && id !== undefined),
    );

    const adultGuests =
        reservation.Guests?.filter((g) => {
            const visaInfo = reservation.VisaInfo?.find((v) => v.customerId === g.id);

            if (visaInfo?.id && adultVisaInfoIds.has(visaInfo.id)) return true;
            if (visaInfo?.id && childVisaInfoIds.has(visaInfo.id)) return false;

            if (!g.dateOfBirth) return true;
            return Number(dayjs().diff(g.dateOfBirth, 'years')) >= 18;
        }) || [];

    const childGuests =
        reservation.Guests?.filter((g) => {
            const visaInfo = reservation.VisaInfo?.find((v) => v.customerId === g.id);

            if (visaInfo?.id && childVisaInfoIds.has(visaInfo.id)) return true;
            if (visaInfo?.id && adultVisaInfoIds.has(visaInfo.id)) return false;

            if (!g.dateOfBirth) return false;
            return Number(dayjs().diff(g.dateOfBirth, 'years')) < 18;
        }) || [];

    // ========================================================================
    // VIRTUAL ADULTS
    // ========================================================================
    const adultsConfigs = guestTaxConfig?.adults || [];
    const emptyAdultSlotsNeeded = Math.max(0, actualAdultsCount - adultGuests.length);
    const emptyAdultSlotData = adultsConfigs
        .map((config, idx) => ({ config, idx }))
        .filter(({ config }) => config.visaInfoId === null || config.visaInfoId === undefined)
        .slice(0, emptyAdultSlotsNeeded);

    const virtualAdults: React.ReactNode[] = [];

    for (let i = 0; i < emptyAdultSlotData.length; i++) {
        const configIndex = emptyAdultSlotData[i]?.idx;
        const existingAdultConfig = adultsConfigs[configIndex];

        const suggestedTax = getSuggestedItemByAge(30, 'tax', taxes, defaultPosItems, 'adult');
        const suggestedInsurance = getSuggestedItemByAge(30, 'insurance', taxes, defaultPosItems, 'adult');

        const adultConfig = {
            visaInfoId: existingAdultConfig?.visaInfoId ?? null,
            fixedTaxId:
                existingAdultConfig?.fixedTaxId !== undefined && existingAdultConfig?.fixedTaxId !== null
                    ? existingAdultConfig.fixedTaxId
                    : suggestedTax.id,
            fixedTaxId2:
                existingAdultConfig?.fixedTaxId2 !== undefined && existingAdultConfig?.fixedTaxId2 !== null
                    ? existingAdultConfig.fixedTaxId2
                    : suggestedInsurance.id,
            fixedTaxType:
                existingAdultConfig?.fixedTaxType !== undefined
                    ? existingAdultConfig.fixedTaxType
                    : suggestedTax.sourceType,
            checkInTimestamp: existingAdultConfig?.checkInTimestamp ?? null,
            checkOutTimestamp: existingAdultConfig?.checkOutTimestamp ?? null,
        };

        const isSearchActive = activeAdultSearchIndex === configIndex;
        const hasCustomDates =
            adultConfig.checkInTimestamp &&
            adultConfig.checkOutTimestamp &&
            (adultConfig.checkInTimestamp !== reservation.checkInTimestamp ||
                adultConfig.checkOutTimestamp !== reservation.checkOutTimestamp);

        const adultCheckIn = adultConfig.checkInTimestamp || reservation.checkInTimestamp;
        const adultCheckOut = adultConfig.checkOutTimestamp || reservation.checkOutTimestamp;
        const adultCheckInFormatted = dayjs(adultCheckIn).format('DD.MM.YYYY');
        const adultCheckOutFormatted = dayjs(adultCheckOut).format('DD.MM.YYYY');
        const adultNights = dayjs(adultCheckOut).diff(dayjs(adultCheckIn), 'day');
        const adultDateTooltip = hasCustomDates
            ? `${adultCheckInFormatted} - ${adultCheckOutFormatted} (${adultNights} ${translate('nights')})`
            : translate('Same as reservation');

        virtualAdults.push(
            <div
                key={`virtual-adult-${i}`}
                className="custom-list-item"
                ref={isSearchActive ? searchContainerRef : undefined}
                style={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {isSearchActive ? (
                        <div style={{ width: '150px' }} data-adult-search={configIndex}>
                            <CustomersAndCompanies
                                history={history}
                                customer={null}
                                company={null}
                                showCompany={false}
                                customerSelect={(c) => addGuestToAdultSlot(c, configIndex)}
                                companySelect={() => {}}
                                reservation={reservation}
                                docScanner={false}
                                width="140px"
                            />
                        </div>
                    ) : (
                        <>
                            <span
                                className="pointer"
                                style={{ fontSize: '13px', color: '#1976d2' }}
                                onClick={() => setActiveAdultSearchIndex(configIndex)}
                                title={translate('Click to search guest')}
                            >
                                {translate('Adult')} {configIndex + 1}
                            </span>
                            <button
                                className="button-light ml-4p"
                                onClick={() => setActiveAdultSearchIndex(configIndex)}
                                style={{ fontSize: '10px', padding: '2px 4px', minWidth: 'auto' }}
                                title={translate('Search guest')}
                            >
                                <i className="fa fa-search" />
                            </button>
                            <button
                                className="button-light ml-2p"
                                onClick={() => createNewCustomerForAdult(configIndex)}
                                style={{ fontSize: '10px', padding: '2px 4px', minWidth: 'auto' }}
                                title={translate('New customer')}
                            >
                                <i className="fa fa-plus" />
                            </button>
                        </>
                    )}
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {(reservation.adultsNumber || 1) > 1 && (
                    <div
                        onClick={() => {
                            setModalContent(
                                <VirtualGuestStayModalContent
                                    guestType="adult"
                                    configIndex={configIndex}
                                    guestLabel={`${translate('Adult')} ${configIndex + 1}`}
                                    currentCheckIn={adultConfig.checkInTimestamp}
                                    currentCheckOut={adultConfig.checkOutTimestamp}
                                    resCheckIn={reservation.checkInTimestamp}
                                    resCheckOut={reservation.checkOutTimestamp}
                                    onSave={(checkIn, checkOut) => handleAdultDateSave(configIndex, checkIn, checkOut)}
                                />,
                                translate(`The guest's stay`),
                                false,
                                'modal-md',
                            );
                        }}
                        className="pointer"
                        style={{ width: '20px', textAlign: 'center' }}
                    >
                        <Tooltip message={adultDateTooltip} style={{ zIndex: 9999, position: 'absolute' }}>
                            <i
                                className={hasCustomDates ? 'fa fa-check-square' : 'fa fa-square'}
                                style={{ color: hasCustomDates ? '#28a745' : '#6c757d' }}
                            ></i>
                        </Tooltip>
                    </div>
                    )}

                    {allTaxOptions.length > 0 && (
                        <select
                            onChange={(e) => handleAdultTaxChange(configIndex, 'fixedTaxId', e.target.value)}
                            value={getDropdownValue(adultConfig.fixedTaxId, adultConfig.fixedTaxType)}
                            className="input input-modal"
                            style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                        >
                            {allTaxOptions.map((opt: DropdownOption, key: number) => (
                                <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                    {translate(opt.name)}
                                </option>
                            ))}
                            <option value="0">{translate('No charge')}</option>
                        </select>
                    )}

                    {allInsuranceOptions.length > 0 && (
                        <select
                            onChange={(e) => handleAdultTaxChange(configIndex, 'fixedTaxId2', e.target.value)}
                            value={getDropdownValue(adultConfig.fixedTaxId2, adultConfig.fixedTaxType)}
                            className="input input-modal"
                            style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                        >
                            {allInsuranceOptions.map((opt: DropdownOption, key: number) => (
                                <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                    {translate(opt.name)}
                                </option>
                            ))}
                            <option value="0">{translate('No charge')}</option>
                        </select>
                    )}
                </div>
            </div>,
        );
    }

    // ========================================================================
    // VIRTUAL CHILDREN
    // ========================================================================
    const childrenConfigs = guestTaxConfig?.children || [];
    const emptyChildSlotsNeeded = Math.max(0, childrenCount - childGuests.length);
    const emptyChildSlotData = childrenConfigs
        .map((config, idx) => ({ config, idx }))
        .filter(({ config }) => config.visaInfoId === null || config.visaInfoId === undefined)
        .slice(0, emptyChildSlotsNeeded);
    const virtualChildren: React.ReactNode[] = [];

    for (let i = 0; i < emptyChildSlotData.length; i++) {
        const configIndex = emptyChildSlotData[i]?.idx;
        const existingConfig = childrenConfigs[configIndex];
        const childAge = existingConfig?.age >= 0 ? existingConfig.age : 5;

        const suggestedTax = getSuggestedItemByAge(childAge, 'tax', taxes, defaultPosItems, 'child');
        const suggestedInsurance = getSuggestedItemByAge(childAge, 'insurance', taxes, defaultPosItems, 'child');

        const childConfig = {
            age: existingConfig?.age ?? -1,
            visaInfoId: existingConfig?.visaInfoId ?? null,
            fixedTaxId:
                existingConfig?.fixedTaxId !== undefined && existingConfig?.fixedTaxId !== null
                    ? existingConfig.fixedTaxId
                    : suggestedTax.id,
            fixedTaxId2:
                existingConfig?.fixedTaxId2 !== undefined && existingConfig?.fixedTaxId2 !== null
                    ? existingConfig.fixedTaxId2
                    : suggestedInsurance.id,
            fixedTaxType:
                existingConfig?.fixedTaxType !== undefined ? existingConfig.fixedTaxType : suggestedTax.sourceType,
            checkInTimestamp: existingConfig?.checkInTimestamp ?? null,
            checkOutTimestamp: existingConfig?.checkOutTimestamp ?? null,
        };

        const needsAge = childConfig.age < 0;
        const isSearchActive = activeChildSearchIndex === configIndex;
        const hasCustomDates =
            childConfig.checkInTimestamp &&
            childConfig.checkOutTimestamp &&
            (childConfig.checkInTimestamp !== reservation.checkInTimestamp ||
                childConfig.checkOutTimestamp !== reservation.checkOutTimestamp);

        const childCheckIn = childConfig.checkInTimestamp || reservation.checkInTimestamp;
        const childCheckOut = childConfig.checkOutTimestamp || reservation.checkOutTimestamp;
        const childCheckInFormatted = dayjs(childCheckIn).format('DD.MM.YYYY');
        const childCheckOutFormatted = dayjs(childCheckOut).format('DD.MM.YYYY');
        const childNights = dayjs(childCheckOut).diff(dayjs(childCheckIn), 'day');
        const childDateTooltip = hasCustomDates
            ? `${childCheckInFormatted} - ${childCheckOutFormatted} (${childNights} ${translate('nights')})`
            : translate('Same as reservation');

        virtualChildren.push(
            <div
                key={`virtual-child-${configIndex}`}
                className="custom-list-item"
                ref={isSearchActive ? searchContainerRef : undefined}
                style={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}
            >
                <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {isSearchActive ? (
                        <div style={{ width: '150px' }} data-child-search={configIndex}>
                            <CustomersAndCompanies
                                history={history}
                                customer={null}
                                company={null}
                                showCompany={false}
                                customerSelect={(c) => addGuestToChildSlot(c, configIndex)}
                                companySelect={() => {}}
                                reservation={reservation}
                                docScanner={false}
                                width="140px"
                            />
                        </div>
                    ) : (
                        <>
                            <span
                                className="pointer"
                                style={{ fontSize: '13px', color: '#007bff' }}
                                onClick={() => setActiveChildSearchIndex(configIndex)}
                                title={translate('Click to search guest')}
                            >
                                {translate('Child')} {configIndex + 1}
                            </span>
                            <button
                                className="button-light ml-4p"
                                onClick={() => setActiveChildSearchIndex(configIndex)}
                                style={{ fontSize: '10px', padding: '2px 4px', minWidth: 'auto' }}
                                title={translate('Search guest')}
                            >
                                <i className="fa fa-search" />
                            </button>
                            <button
                                className="button-light ml-2p"
                                onClick={() => createNewCustomerForChild(configIndex)}
                                style={{ fontSize: '10px', padding: '2px 4px', minWidth: 'auto' }}
                                title={translate('New customer')}
                            >
                                <i className="fa fa-plus" />
                            </button>
                        </>
                    )}
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <select
                        value={childConfig.age}
                        onChange={(e) => handleChildAgeChange(configIndex, Number(e.target.value))}
                        className="input input-modal"
                        style={{
                            width: '60px',
                            fontSize: '11px',
                            padding: '2px 4px',
                            borderColor: needsAge ? '#ffc107' : '#ced4da',
                            backgroundColor: needsAge ? '#fff3cd' : 'white',
                        }}
                    >
                        <option value={-1}>?</option>
                        {Array.from({ length: 18 }, (_, idx) => (
                            <option key={idx} value={idx}>
                                {idx} {translate('yrs')}
                            </option>
                        ))}
                    </select>

                    <div
                        onClick={() => {
                            setModalContent(
                                <VirtualGuestStayModalContent
                                    guestType="child"
                                    configIndex={configIndex}
                                    guestLabel={`${translate('Child')} ${configIndex + 1}`}
                                    currentCheckIn={childConfig.checkInTimestamp}
                                    currentCheckOut={childConfig.checkOutTimestamp}
                                    resCheckIn={reservation.checkInTimestamp}
                                    resCheckOut={reservation.checkOutTimestamp}
                                    onSave={(checkIn, checkOut) => handleChildDateSave(configIndex, checkIn, checkOut)}
                                />,
                                translate(`The guest's stay`),
                                false,
                                'modal-md',
                            );
                        }}
                        className="pointer"
                        style={{ width: '20px', textAlign: 'center' }}
                    >
                        <Tooltip message={childDateTooltip} style={{ zIndex: 9999, position: 'absolute' }}>
                            <i
                                className={hasCustomDates ? 'fa fa-check-square' : 'fa fa-square'}
                                style={{ color: hasCustomDates ? '#28a745' : '#6c757d' }}
                            ></i>
                        </Tooltip>
                    </div>

                    {allTaxOptions.length > 0 && (
                        <select
                            onChange={(e) => handleChildTaxChange(configIndex, 'fixedTaxId', e.target.value)}
                            value={getDropdownValue(childConfig.fixedTaxId, childConfig.fixedTaxType)}
                            className="input input-modal"
                            style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                        >
                            {allTaxOptions.map((opt: DropdownOption, key: number) => (
                                <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                    {translate(opt.name)}
                                </option>
                            ))}
                            <option value="0">{translate('No charge')}</option>
                        </select>
                    )}

                    {allInsuranceOptions.length > 0 && (
                        <select
                            onChange={(e) => handleChildTaxChange(configIndex, 'fixedTaxId2', e.target.value)}
                            value={getDropdownValue(childConfig.fixedTaxId2, childConfig.fixedTaxType)}
                            className="input input-modal"
                            style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                        >
                            {allInsuranceOptions.map((opt: DropdownOption, key: number) => (
                                <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                    {translate(opt.name)}
                                </option>
                            ))}
                            <option value="0">{translate('No charge')}</option>
                        </select>
                    )}
                </div>
            </div>,
        );
    }

    // ========================================================================
    // REAL GUESTS LISTS
    // ========================================================================
    let adultGuestsList = null;
    let childGuestsList = null;

    if (adultGuests.length === 0 && virtualAdults.length === 0) {
        adultGuestsList = (
            <div className="col-md-3">
                <div className="p-3 bg-white border rounded text-center">{translate('List is empty')}</div>
            </div>
        );
    } else {
        adultGuestsList = adultGuests.map((customer, index: number) => {
            return (
                <CustomerInfo
                    key={`adult-${index}`}
                    index={index}
                    customer={customer}
                    editCustomer={editCustomer}
                    visaDataModal={visaDataModal}
                    showDeleteModal={showDeleteModal}
                    invalidVisaIds={ctrl.getCustomersWithInvalidVisaIds()}
                    reservationHolderId={reservation.customerId ? reservation.customerId : -1}
                    reservation={reservation}
                    location={location}
                    contextMenuIndex={contextMenuIndex}
                    setContextMenuIndex={setContextMenuIndex}
                    points={points}
                    setPoints={setPoints}
                    otherSettings={props.otherSettings}
                    taxes={taxes}
                    defaultPosItems={defaultPosItems}
                />
            );
        });
    }

    if (childGuests.length > 0) {
        childGuestsList = childGuests.map((customer, index: number) => {
            return (
                <CustomerInfo
                    key={`child-${index}`}
                    index={adultGuests.length + index}
                    customer={customer}
                    editCustomer={editCustomer}
                    visaDataModal={visaDataModal}
                    showDeleteModal={showDeleteModal}
                    invalidVisaIds={ctrl.getCustomersWithInvalidVisaIds()}
                    reservationHolderId={reservation.customerId ? reservation.customerId : -1}
                    reservation={reservation}
                    location={location}
                    contextMenuIndex={contextMenuIndex}
                    setContextMenuIndex={setContextMenuIndex}
                    points={points}
                    setPoints={setPoints}
                    otherSettings={props.otherSettings}
                    taxes={taxes}
                    defaultPosItems={defaultPosItems}
                />
            );
        });
    }

    if (!reservation.id || reservation.id === null) {
        return null;
    }

    return (
        <div className="reservation-inner-height">
            <div className="mt-4p mb-8p">
                <CustomersAndCompanies
                    history={history}
                    customer={null}
                    company={null}
                    showCompany={false}
                    customerSelect={addGuestFromGeneralSearch}
                    companySelect={() => {}}
                    reservation={reservation}
                    docScanner={true}
                    width="400px"
                />
            </div>

            <div style={{ minHeight: '400px', maxHeight: '400px', overflowY: 'auto' }}>
                {(adultGuests.length > 0 || virtualAdults.length > 0) && (
                    <>
                        <div
                            className="mb-4p text-bold"
                            style={{
                                fontSize: '12px',
                                color: '#1976d2',
                                borderBottom: '1px solid #1976d2',
                                paddingBottom: '4px',
                            }}
                        >
                            {translate('Adults')} ({adultGuests.length}/{actualAdultsCount})
                        </div>
                        {adultGuestsList}
                        {virtualAdults}
                    </>
                )}

                {childrenCount > 0 && (
                    <>
                        <div
                            className="mt-12p mb-4p text-bold"
                            style={{
                                fontSize: '12px',
                                color: '#28a745',
                                borderBottom: '1px solid #28a745',
                                paddingBottom: '4px',
                            }}
                        >
                            {translate('Children')} ({childGuests.length}/{childrenCount})
                        </div>
                        {childGuestsList}
                        {virtualChildren}
                    </>
                )}
            </div>
        </div>
    );
};

// ============================================================================
// CUSTOMER INFO COMPONENT - BEZ AUTO-SPREMANJA
// ============================================================================
const CustomerInfo = (props: CustomerInfoProps) => {
    const {
        customer,
        index,
        editCustomer,
        visaDataModal,
        reservationHolderId,
        showDeleteModal,
        invalidVisaIds,
        reservation,
        location,
        contextMenuIndex,
        setContextMenuIndex,
        points,
        setPoints,
        taxes,
        defaultPosItems,
    } = props;

    const country = countryCodes.find((cInfo: any) => {
        return customer.country === cInfo['country-code'];
    });

    const disableAccommodationTaxCheckbox: boolean =
        location && location.pathname.lastIndexOf('reservationDetails') !== -1 ? true : false;

    const visaData: ReservationGuestReferenceModel | undefined = reservation.VisaInfo?.find(
        (v) => v.customerId === customer.id,
    );

    const customerData =
        (customer.firstName ? customer.firstName : '') +
        ' ' +
        (customer.lastName ? customer.lastName + '\n' : '') +
        (customer.email ? customer.email + '\n' : '') +
        (customer.phoneOrMobile ? customer.phoneOrMobile + '\n' : '') +
        (customer.address ? customer.address + ', ' : '') +
        (customer.town ? customer.town + '\n' : '') +
        (country ? country['alpha-3'] : '');

    const options = [];
    options.push({ label: 'Visa data modal', icon: <Report /> });
    if (reservation.customerId !== customer.id) {
        options.push({ label: 'Edit', icon: <Edit /> });
    }
    if (reservationHolderId !== customer.id) {
        options.push({ label: 'Delete', icon: <DeleteIcon /> });
    }

    const isHolder = reservation.customerId === customer.id;

    const customerAge = customer.dateOfBirth ? Number(dayjs().diff(customer.dateOfBirth, 'years')) : null;
    const isChild = customerAge !== null && customerAge < 18;

    const adultTaxOpts = getDropdownOptions(taxes, defaultPosItems, 'tax', 'adult');
    const childTaxOpts = getDropdownOptions(taxes, defaultPosItems, 'tax', 'child');
    const adultInsOpts = getDropdownOptions(taxes, defaultPosItems, 'insurance', 'adult');
    const childInsOpts = getDropdownOptions(taxes, defaultPosItems, 'insurance', 'child');

    const taxOptions = [...adultTaxOpts, ...childTaxOpts].filter(
        (opt, idx, self) => idx === self.findIndex((o) => o.id === opt.id && o.sourceType === opt.sourceType),
    );

    const insuranceOptions = [...adultInsOpts, ...childInsOpts].filter(
        (opt, idx, self) => idx === self.findIndex((o) => o.id === opt.id && o.sourceType === opt.sourceType),
    );

    // Dohvati vrijednosti iz guestTaxConfig (PRIMARNI IZVOR!)
    const guestTaxConfig = parseGuestTaxConfig(reservation);
    let currentFixedTaxId: number | null = null;
    let currentFixedTaxId2: number | null = null;
    let currentFixedTaxType: 'tax' | 'posItem' | null = null;
    let hasCustomDates = false;
    let guestCheckIn: number | null = null;
    let guestCheckOut: number | null = null;

    if (visaData?.id && guestTaxConfig) {
        const adultConfig = guestTaxConfig.adults.find((a) => a.visaInfoId === visaData.id);
        const childConfig = guestTaxConfig.children.find((c) => c.visaInfoId === visaData.id);
        const config = adultConfig || childConfig;

        if (config) {
            currentFixedTaxId = config.fixedTaxId ?? null;
            currentFixedTaxId2 = config.fixedTaxId2 ?? null;
            currentFixedTaxType = config.fixedTaxType ?? null;
            guestCheckIn = config.checkInTimestamp || reservation.checkInTimestamp;
            guestCheckOut = config.checkOutTimestamp || reservation.checkOutTimestamp;

            hasCustomDates = !!(
                config.checkInTimestamp &&
                config.checkOutTimestamp &&
                (config.checkInTimestamp !== reservation.checkInTimestamp ||
                    config.checkOutTimestamp !== reservation.checkOutTimestamp)
            );
        }
    }

    // Fallback na visaData ako nema u configu
    if (currentFixedTaxId === null && visaData?.fixedTaxId) {
        currentFixedTaxId = visaData.fixedTaxId;
    }
    if (currentFixedTaxId2 === null && visaData?.fixedTaxId2) {
        currentFixedTaxId2 = visaData.fixedTaxId2;
    }
    if (currentFixedTaxType === null) {
        currentFixedTaxType = (visaData as any)?.fixedTaxType || 'tax';
    }

    // Fallback na reservation datume
    if (!guestCheckIn) guestCheckIn = reservation.checkInTimestamp;
    if (!guestCheckOut) guestCheckOut = reservation.checkOutTimestamp;

    // Formatiraj datume za tooltip
    const checkInFormatted = dayjs(guestCheckIn).format('DD.MM.YYYY');
    const checkOutFormatted = dayjs(guestCheckOut).format('DD.MM.YYYY');
    const nights = dayjs(guestCheckOut).diff(dayjs(guestCheckIn), 'day');
    const dateTooltip = hasCustomDates
        ? `${checkInFormatted} - ${checkOutFormatted} (${nights} ${translate('nights')})`
        : translate('Same as reservation');

    const handleGuestStayClick = () => {
        const { checkInTimestamp, checkOutTimestamp } = reservation;

        if (!checkInTimestamp || !checkOutTimestamp) return;
        if (!visaData?.customerId || !visaData?.id) return;

        const guest = reservation?.Guests?.find((g) => g.id === visaData?.customerId);
        const guestName = `${guest?.firstName || ''} ${guest?.lastName || ''}`;

        setModalContent(
            <GuestStayModalContent
                customerId={visaData.customerId}
                visaInfoId={visaData.id}
                guestName={guestName}
                resCheckIn={checkInTimestamp}
                resCheckOut={checkOutTimestamp}
            />,
            translate(`The guest's stay`),
            false,
            'modal-md',
        );
    };

    return (
        <div
            key={'customer' + index}
            className={
                'custom-list-item ' +
                (contextMenuIndex !== null && contextMenuIndex !== undefined
                    ? contextMenuIndex !== index
                        ? ' hover-disabled'
                        : ' custom-list-item-selected'
                    : ' ')
            }
            style={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}
        >
            {/* LIJEVI DIO - ime gosta */}
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {customer && customer.id && invalidVisaIds.includes(customer.id) ? (
                    <div className="pr-4p">
                        <Tooltip
                            message={translate(`Visa information is not filled out`)}
                            style={{ zIndex: 9999, position: 'absolute' }}
                        >
                            <i className="fa fa-exclamation-circle text-danger" aria-hidden="true"></i>
                        </Tooltip>
                    </div>
                ) : null}

                <span
                    className="text-bold pointer"
                    onClick={() =>
                        setModalContent(
                            <textarea
                                rows={5}
                                cols={20}
                                style={{ whiteSpace: 'pre-wrap' }}
                                disabled
                                onChange={() => {}}
                                value={customerData}
                                className="input input-modal"
                            ></textarea>,
                            translate('Guest data'),
                            false,
                            'modal-md',
                        )
                    }
                    style={{ fontSize: '13px' }}
                >
                    {`${customer.firstName ? customer.firstName : ''} ${customer.lastName ? customer.lastName : ''}`}
                </span>

                {customerAge !== null && (
                    <span
                        style={{
                            color: isChild ? '#28a745' : '#6c757d',
                            fontSize: '11px',
                            marginLeft: '4px',
                        }}
                    >
                        ({customerAge} {translate('yrs')})
                    </span>
                )}

                {isHolder && (
                    <span
                        className="ml-4p"
                        style={{
                            fontSize: '10px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            padding: '1px 6px',
                            borderRadius: '10px',
                        }}
                    >
                        {translate('Holder')}
                    </span>
                )}

                {customer.email && (
                    <Tooltip message={customer.email}>
                        <i
                            className="fa fa-envelope ml-4p"
                            style={{ fontSize: '11px', color: '#6c757d', cursor: 'pointer' }}
                        />
                    </Tooltip>
                )}
            </div>
            {/* Residence Tax Reduction select */}
            <PermissionCheck name="dashboard_cisu">
                <select
                    value={visaData?.residenceTaxReductionCode ?? ''}
                    onChange={(e) => {
                        if (visaData && visaData.id) {
                            const freshReservation = ctrl.getActiveReservation();
                            const updatedVisaData = freshReservation.VisaInfo?.find((v) => v.id === visaData.id);
                            if (!updatedVisaData) return;
                            ctrl.updateReservationGuestReference({
                                ...updatedVisaData,
                                residenceTaxReductionCode: e.target.value === '' ? null : e.target.value,
                            });
                        }
                    }}
                    className="input input-modal"
                    style={{ width: '120px', fontSize: '11px', padding: '2px 4px' }}
                >
                    <option value="">{translate('No reduction')}</option>
                    <option value={ResidenceTaxReductionCodeCISU.childrenUnder12}>
                        {translate('Children under 12')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.spaClimateTreatmentReferral}>
                        {translate('Spa/climate treatment - referral')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.spaClimateTreatmentIndividual}>
                        {translate('Spa/climate treatment - individual')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.personsWithDisabilities}>
                        {translate('Persons with disabilities')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.childrenWithDevelopmentalDisabilitiesForeignCare}>
                        {translate('Children with developmental disabilities - foreign care')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.adultsWithDisabilitiesForeignCare}>
                        {translate('Adults with disabilities - foreign care')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.studentsOrganizedStay}>
                        {translate('Students - organized stay')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.studentsSchooling}>
                        {translate('Students - schooling')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.foreignNationalsTaxExempt}>
                        {translate('Foreign nationals tax exempt')}
                    </option>
                    <option value={ResidenceTaxReductionCodeCISU.youth12To18}>{translate('Youth 12-18')}</option>
                    <option value={ResidenceTaxReductionCodeCISU.stayLongerThan30Days}>
                        {translate('Stay longer than 30 days')}
                    </option>
                </select>
            </PermissionCheck>
            {/* DESNI DIO - kontrole */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Checkbox za datume - prikazuje se samo ako ima više od jednog odraslog */}
                {(reservation.adultsNumber || 1) > 1 && (
                <div onClick={handleGuestStayClick} className="pointer" style={{ width: '20px', textAlign: 'center' }}>
                    <Tooltip message={dateTooltip} style={{ zIndex: 9999, position: 'absolute' }}>
                        <i
                            className={hasCustomDates ? 'fa fa-check-square' : 'fa fa-square'}
                            style={{ color: hasCustomDates ? '#28a745' : '#6c757d' }}
                        ></i>
                    </Tooltip>
                </div>
                )}

                {/* Tax dropdown - SAMO LOKALNO! */}
                {taxOptions.length > 0 && (
                    <select
                        onChange={(e) => {
                            if (visaData && visaData.id) {
                                const parsed = parseDropdownValue(e.target.value);
                                // SAMO LOKALNO - bez API poziva!
                                syncTaxChangeToGuestConfig(
                                    visaData.id,
                                    'fixedTaxId',
                                    parsed.id,
                                    parsed.sourceType,
                                    taxes,
                                    defaultPosItems,
                                );
                            }
                        }}
                        name="fixedTaxId"
                        value={getDropdownValue(currentFixedTaxId, currentFixedTaxType)}
                        className="input input-modal"
                        disabled={disableAccommodationTaxCheckbox}
                        style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                    >
                        {taxOptions.map((opt: DropdownOption, key: number) => (
                            <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                {translate(opt.name)}
                            </option>
                        ))}
                        <option value="0">{translate('No charge')}</option>
                    </select>
                )}

                {/* Insurance dropdown - SAMO LOKALNO! */}
                {insuranceOptions.length > 0 && (
                    <select
                        onChange={(e) => {
                            if (visaData && visaData.id) {
                                const parsed = parseDropdownValue(e.target.value);
                                // SAMO LOKALNO - bez API poziva!
                                syncTaxChangeToGuestConfig(
                                    visaData.id,
                                    'fixedTaxId2',
                                    parsed.id,
                                    parsed.sourceType,
                                    taxes,
                                    defaultPosItems,
                                );
                            }
                        }}
                        name="fixedTaxId2"
                        value={getDropdownValue(currentFixedTaxId2, currentFixedTaxType)}
                        className="input input-modal"
                        disabled={disableAccommodationTaxCheckbox}
                        style={{ width: '100px', fontSize: '11px', padding: '2px 4px' }}
                    >
                        {insuranceOptions.map((opt: DropdownOption, key: number) => (
                            <option value={getDropdownValue(opt.id, opt.sourceType)} key={key}>
                                {translate(opt.name)}
                            </option>
                        ))}
                        <option value="0">{translate('No charge')}</option>
                    </select>
                )}

                {/* Context menu */}
                <ContextMenu
                    options={options}
                    position={index ? index : 0}
                    contextMenuIndex={contextMenuIndex}
                    setContextMenuIndex={setContextMenuIndex}
                    points={points}
                    setPoints={setPoints}
                    onSelect={async (option: string) => {
                        if (customer?.id) {
                            if (option === 'Visa data modal') {
                                visaDataModal(customer);
                            } else if (option === 'Edit') {
                                editCustomer(customer);
                            } else if (option === 'Delete') {
                                showDeleteModal(customer);
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default VisaInfo;
